import { Injectable, inject } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, from, map } from 'rxjs';
import { PermissionService } from '../services/permission.service';

@Injectable({ providedIn: 'root' })
export class PermissionGuard implements CanActivate {
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | Observable<boolean> {
    if (!this.permissionService.isSignedIn()) {
      this.router.navigate(['']);
      return false;
    }

    // The session permission is the user's own widest role (derived from
    // GET /me/). It is NOT impersonation — impersonation is opt-in.
    //
    // When it isn't set yet, WAIT for the roles rather than bouncing. This runs
    // moments after an OAuth return, so bouncing here ate the very deep link
    // the sign-in was supposed to restore: the landing resolver would navigate
    // to /academic-officer/…/profile and this guard would immediately throw it
    // away because GET /me/ had not answered yet. waitForRoles() gives up after
    // ten seconds so a hung /me/ shows a page rather than a blank screen.
    if (!this.permissionService.selectedPermission()) {
      return from(this.permissionService.waitForRoles()).pipe(
        map(() => this.evaluate(route, state)),
      );
    }

    return this.evaluate(route, state);
  }

  /** The decision itself, once roles are as resolved as they are going to get. */
  private evaluate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    let permission = this.permissionService.selectedPermission();
    if (!permission) {
      const own = this.permissionService.defaultPermissionForRoles();
      if (own) {
        this.permissionService.selectPermission(own);
        permission = own;
      } else {
        // Roles answered with nothing usable (or never answered at all). There
        // is no basis to judge this route, so send them home — which for a
        // signed-in user is a page, not a refusal.
        const home = this.permissionService.homeRoute();
        if (state.url.split('?')[0] === home) return true;
        this.router.navigateByUrl(home);
        return false;
      }
    }

    // Check route-specific permissions
    const requiredGroups = route.data?.['requiredGroups'] as string[] || [];
    if (requiredGroups.length > 0) {
      if (!this.permissionService.canAccessRoute(requiredGroups)) {
        this.router.navigate(['/unauthorized']);
        return false;
      }
    }

    // While impersonating, keep the officer URL consistent with the officer
    // being viewed: /academic-officer/:id must match the selected officer.
    //
    // Scoped to that route by path. Applied to every route carrying an `:id`,
    // it compared unrelated identifiers — a leave request id against an emp_id
    // — and bounced the user back to the officer dashboard. Clicking a leave
    // request while viewing as an officer simply did nothing visible, since the
    // redirect landed on the page they were already on.
    if (this.permissionService.isImpersonating() && this.isOfficerScopedRoute(state.url)) {
      const routeId = route.paramMap.get('id');
      const officer = this.permissionService.selectedAcademicOfficer();
      if (routeId && officer?.emp_id && String(officer.emp_id) !== String(routeId)) {
        this.router.navigate(['/academic-officer', officer.emp_id]);
        return false;
      }
    }

    return true;
  }

  /**
   * True when the route's `:id` is an academic officer's emp_id.
   *
   * Only `/academic-officer/:id...` uses it that way. Every other `:id` in the
   * app identifies something else entirely — a leave request, a PAC review —
   * and must not be compared against the impersonated officer.
   */
  private isOfficerScopedRoute(url: string): boolean {
    return url.split('?')[0].startsWith('/academic-officer/');
  }
}
